import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import allAcronyms from "@/data/allAcronyms";
import {
  BookOpen, Check, Lock, ArrowRight, ShoppingBag, Mail, User, Gift,
  Shield, Heart, Star, Sparkles, Zap, Target, Users, Briefcase, Crown, Wand2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FULL_EDITION_STRIPE = "https://buy.stripe.com/5kQ28k7DS1BJaee9SSc7u0m";

const categoryStyles = {
  core: { color: "from-[#25a1c2] to-[#042F2E]", bg: "bg-[#F0FDF4]", border: "border-[#25a1c2]/20" },
  personal: { color: "from-[#7C3AED] to-[#4F46E5]", bg: "bg-[#F5F3FF]", border: "border-[#7C3AED]/20" },
  tools: { color: "from-[#B45309] to-[#D97706]", bg: "bg-[#FFFBEB]", border: "border-[#D97706]/20" },
  family: { color: "from-[#EA580C] to-[#F59E0B]", bg: "bg-[#FFF7ED]", border: "border-[#EA580C]/20" },
  business: { color: "from-[#1E40AF] to-[#3B82F6]", bg: "bg-[#EFF6FF]", border: "border-[#1E40AF]/20" },
  wellness: { color: "from-[#25a1c2] to-[#068fa2]", bg: "bg-[#F0FDF4]", border: "border-[#25a1c2]/20" },
  empowerment: { color: "from-[#DC2626] to-[#E11D48]", bg: "bg-[#FEF2F2]", border: "border-[#DC2626]/20" },
};

const productLinks = {
  "Personal Code of Conduct": { name: "PCC Workbook", price: 17, link: "https://buy.stripe.com/aFa28k8HW1BJbiie58c7u0b" },
  "Release Old Ways": { name: "Mindset Shift Workbook", price: 14, link: "https://buy.stripe.com/00w6oAbU83JRbii6GGc7u0c" },
};

const AcronymCard = ({ acronym, index }) => {
  const style = categoryStyles[acronym.category] || categoryStyles.personal;
  const product = productLinks[acronym.title] || null;

  return (
    <div
      className="mb-10 print:page-break-inside-avoid"
      data-testid={`acronym-${acronym.code.toLowerCase().replace(/[^a-z]/g, "")}`}
    >
      <div className={`${style.bg} ${style.border} border-2 rounded-2xl overflow-hidden`}>
        <div className={`bg-gradient-to-r ${style.color} px-6 py-4`}>
          <h3 className="text-white font-bold text-lg tracking-wide">{acronym.code}</h3>
          <p className="text-white/60 text-xs uppercase tracking-widest">{acronym.title}</p>
        </div>
        <div className="p-6 md:p-8">
          <div className="space-y-4">
            {acronym.letters.map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${style.color} flex items-center justify-center shrink-0`}>
                  <span className="text-white font-bold text-sm">{item.letter}</span>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#042F2E] text-sm">{item.word}</p>
                  {item.definition && (
                    <p className="text-[#475569] text-sm leading-relaxed mt-0.5">{item.definition}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {product && (
            <div className="mt-6 pt-4 border-t border-dashed border-gray-200 flex items-center justify-between">
              <p className="text-xs text-gray-400">Go deeper with this code:</p>
              <a
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium transition-colors"
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                {product.name} — ${product.price}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AcronymDictionary() {
  const [formData, setFormData] = useState({ firstName: "", email: "" });
  const [unlocked, setUnlocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "The I GET TO Acronym Dictionary — 38 Codes for Life | Mind Body Spirit HYGIENE™ Tools";
    if (localStorage.getItem("mbsh_acronym_unlocked")) setUnlocked(true);
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/subscribe`, formData);
    } catch {}
    setUnlocked(true);
    localStorage.setItem("mbsh_acronym_unlocked", "true");
    setIsSubmitting(false);
  };

  return (
    <div className="bg-[#efeae7] min-h-screen print:bg-white" data-testid="acronym-dictionary-page">
      {/* Header */}
      <header className="print:hidden fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/downloads/MBSH-logo-TM-e4476e.png"
              alt="Mind Body Spirit HYGIENE™ Tools"
              className="h-12 w-auto"
            />
          </Link>
          <nav className="flex items-center gap-4 md:gap-8">
            <Link to="/" className="font-sans text-[#068fa2] hover:text-[#25a1c2] text-sm transition-colors hidden md:block">Shop</Link>
            <Link to="/build-your-own" className="text-[#068fa2] hover:text-[#25a1c2] text-sm font-medium transition-colors" data-testid="acronym-nav-build-your-own">Build Your Own</Link>
            <button
              onClick={() => window.print()}
              className="bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-5 py-2.5 font-sans font-medium text-sm transition-all hover:-translate-y-0.5"
              data-testid="print-dictionary-btn"
            >
              Print / Save PDF
            </button>
          </nav>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 pt-24 pb-16 print:pt-8">
        {/* Hero */}
        <div className="text-center mb-16 print:mb-10" data-testid="dictionary-hero">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="bg-[#25a1c2] text-white text-xs font-bold px-3 py-1.5 rounded-full tracking-wide uppercase">Free Sample Edition</span>
          </div>
          <div className="w-16 h-16 bg-[#25a1c2] rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <p className="text-[#25a1c2] text-sm font-bold tracking-widest uppercase mb-3">Mind Body Spirit HYGIENE™ Tools</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#042F2E] tracking-tight mb-4" data-testid="dictionary-title">
            The "I GET TO"<br />Acronym Dictionary
          </h1>
          <p className="text-xl text-[#475569] mb-3">38 Codes for Personal, Family & Business Life</p>
          <p className="text-[#25a1c2] font-medium">By Temple Franklin & the Divine Team</p>

          <div className="mt-8 bg-[#042F2E] rounded-2xl p-6 max-w-lg mx-auto text-left">
            <p className="text-[#5EEAD4] text-xs font-bold uppercase tracking-widest mb-2">How to Use This Dictionary</p>
            <p className="text-white/80 text-sm leading-relaxed mb-3">
              Think of these codes like trying on hats — so many designs, shapes. You can wear it straight, backwards, sideways.
              "Try on" the tools, see what fits for you — just commit to using them, finding your own, and integrating them into your life and lifestyle.
            </p>
            <p className="text-white/80 text-sm leading-relaxed">
              You will feel different because you should — <strong className="text-white">you are evolving, redesigning.</strong>
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-[#475569] flex-wrap">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#25a1c2]" />
              <span>8 free codes below</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#25a1c2]" />
              <span>30 more in the full edition</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#25a1c2]" />
              <span>Printable</span>
            </div>
          </div>
        </div>

        {/* Free Acronyms */}
        <div className="mb-12" data-testid="free-acronyms-section">
          <div className="bg-[#25a1c2] rounded-2xl p-5 mb-8 text-center">
            <p className="text-[#5EEAD4] text-xs font-bold uppercase tracking-widest mb-1">Sample Edition — Free</p>
            <h2 className="text-white text-2xl font-serif">8 Codes to Start Your Journey</h2>
          </div>

          {allAcronyms.filter(a => a.tier === "free").map((acronym, i) => (
            <AcronymCard key={acronym.code} acronym={acronym} index={i} />
          ))}
        </div>

        {/* Email Gate for Free Download */}
        {!unlocked && (
          <div className="print:hidden mb-12 bg-[#042F2E] rounded-3xl p-8 md:p-10 text-center" data-testid="email-gate-section">
            <Gift className="w-10 h-10 text-[#5EEAD4] mx-auto mb-4" />
            <h2 className="font-serif text-2xl md:text-3xl text-white mb-3">
              Want These 5 Codes as a Printable PDF?
            </h2>
            <p className="text-white/70 text-sm mb-6 max-w-md mx-auto">
              Enter your email and get the Sample Edition PDF delivered instantly — plus
              join Temple's free 52-week "I GET TO" email journey and receive a bonus 365-day affirmation calendar.
            </p>
            <form onSubmit={handleUnlock} className="max-w-sm mx-auto space-y-3" data-testid="email-gate-form">
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5EEAD4]" />
                <input
                  type="text"
                  placeholder="First Name"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  data-testid="gate-firstname"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#5EEAD4]"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5EEAD4]" />
                <input
                  type="email"
                  placeholder="Email Address"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="gate-email"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#5EEAD4]"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="gate-submit-btn"
                className="w-full bg-[#5EEAD4] hover:bg-[#99F6E4] text-[#042F2E] rounded-xl py-3.5 font-bold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
              >
                {isSubmitting ? "Joining..." : "Get the Free PDF + Start the Journey"}
              </button>
            </form>
            <p className="text-white/30 text-xs mt-4">No spam. Unsubscribe anytime.</p>
          </div>
        )}

        {unlocked && (
          <div className="print:hidden mb-12 bg-[#F0FDF4] border-2 border-[#25a1c2]/20 rounded-2xl p-6 text-center" data-testid="unlocked-message">
            <Check className="w-8 h-8 text-[#25a1c2] mx-auto mb-2" />
            <p className="text-[#25a1c2] font-bold">You're in! Check your email for the free PDF + calendar.</p>
          </div>
        )}

        {/* Locked Full Edition Preview */}
        <div className="mb-12 print:hidden" data-testid="locked-section">
          <div className="bg-gradient-to-r from-[#7C3AED] to-[#4F46E5] rounded-2xl p-5 mb-6 text-center">
            <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">Full Edition — 38 Codes</p>
            <h2 className="text-white text-2xl font-serif">Unlock the Complete Dictionary</h2>
            <p className="text-white/60 text-sm mt-1">Personal + Wellness + Empowerment + Family + Business Codes</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {allAcronyms.filter(a => a.tier === "paid").map((item) => (
              <div key={item.code} className="bg-white border border-gray-200 rounded-xl p-4 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-gray-300" />
                </div>
                <p className="font-bold text-gray-400 text-sm">{item.code}</p>
                <p className="text-gray-300 text-xs">{item.title}</p>
                <p className="text-gray-300 text-xs">{item.letters.length} principles</p>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-[#042F2E] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl text-white mb-3">Get the Full Acronym Dictionary</h3>
            <p className="text-white/70 text-sm mb-2 max-w-md mx-auto">
              38 codes. 100+ principles. Personal, Wellness, Empowerment, Family & Business life.
              Print it. Post it on your wall. Live by it.
            </p>
            <p className="text-[#5EEAD4] font-serif italic mb-6">
              "Change in your perspective changes your experience."
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={FULL_EDITION_STRIPE}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white hover:bg-[#F0FDF4] text-[#042F2E] rounded-full px-8 py-4 font-bold transition-all hover:-translate-y-1 shadow-lg"
                data-testid="full-edition-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                Get the Full Edition — $8
              </a>
              <Link
                to="/code-builder"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white rounded-full px-8 py-4 font-bold transition-all"
                data-testid="code-builder-link"
              >
                <Wand2 className="w-5 h-5" />
                Build Your Own Code
              </Link>
            </div>
            <p className="text-white/40 text-xs mt-4">Instant PDF download. Print at home.</p>
          </div>
        </div>

        {/* Cross-sell to Products */}
        <div className="print:hidden mb-12" data-testid="crosssell-section">
          <div className="text-center mb-8">
            <p className="text-[#25a1c2] text-sm font-bold tracking-widest uppercase mb-2">Go Deeper</p>
            <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E]">Bring These Codes to Life</h2>
            <p className="text-[#475569] text-sm mt-2">Each code connects to a tool designed to help you integrate it.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: "Personal Code of Conduct Workbook", desc: "17 frameworks including SHIFT, BALANCE, SOAR, POWER with guided prompts", price: 17, link: "https://buy.stripe.com/aFa28k8HW1BJbiie58c7u0b" },
              { name: "Mindset Shift Workbook", desc: "The A.C.T. NOW framework with daily reframing exercises and affirmations", price: 14, link: "https://buy.stripe.com/00w6oAbU83JRbii6GGc7u0c" },
              { name: "The Boundary Builder", desc: "20 scripts for the conversations you've been avoiding. Chapter 3 in action.", price: 14, link: "https://buy.stripe.com/aFacMY9M03JR4TUc10c7u0k" },
              { name: "Emergency Calm Kit", desc: "Breathing cards + grounding techniques for when your nervous system needs a reset", price: 11, link: "https://buy.stripe.com/fZufZaaQ494b8660iic7u0j" },
            ].map((p) => (
              <a
                key={p.name}
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border-2 border-[#CCFBF1] hover:border-[#25a1c2] rounded-2xl p-5 transition-all hover:shadow-lg hover:-translate-y-1 group text-left"
              >
                <h3 className="font-serif text-lg text-[#042F2E] group-hover:text-[#25a1c2] transition-colors mb-1">{p.name}</h3>
                <p className="text-[#475569] text-xs mb-3">{p.desc}</p>
                <span className="text-[#25a1c2] font-bold text-sm">${p.price} — Buy Now</span>
              </a>
            ))}
          </div>
        </div>

        {/* Closing */}
        <div className="bg-[#042F2E] rounded-3xl p-8 md:p-12 text-center" data-testid="closing-section">
          <BookOpen className="w-10 h-10 text-[#5EEAD4] mx-auto mb-4" />
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">
            You Are Your Only Guarantee.<br />This Is YOUR Life.
          </h2>
          <p className="text-white/70 text-sm leading-relaxed max-w-lg mx-auto mb-6">
            These codes are not rules. They are invitations. Try them on like hats — see what fits,
            make them your own, and commit to integrating them into your life and lifestyle.
            You will feel different because you should — you are evolving, redesigning.
          </p>
          <p className="font-serif text-[#5EEAD4] italic text-lg mb-6">
            "Change in your perspective changes your experience."
          </p>
          <p className="text-white/40 text-xs">— Temple Franklin & the Divine Team</p>
          <p className="text-white/30 text-xs mt-1">mindbodyspirithygienetools.com</p>

          <div className="print:hidden mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-1"
              data-testid="shop-cta"
            >
              Explore All Tools
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
